import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import Select from "../../../shared/Select";
import ValidatedInput from "../../../shared/ValidatedInput";
import PhoneInput from "../../../shared/PhoneInput";
import _cloneDeep from "lodash.clonedeep";
import _isEqual from "lodash.isequal";
import { validateEmail } from "../../../../lib";
import SystemApi, { EnumType } from "../../../../services/resources/SystemApi";
import { OtherAttendee } from "./OtherAttendees";

interface SelectOption {
  display: string;
  value: string;
}

interface OtherAttendeeModalProps {
  attendee: OtherAttendee | null;
  close: () => void;
  save: (attendee: OtherAttendee) => void;
  venues: { id: number; name: string }[];
}

const emptyAttendee: OtherAttendee = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  email: "",
  type: "",
  assignedVenueID: undefined,
};

export default function OtherAttendeeModal({
  attendee,
  close,
  save,
  venues,
}: OtherAttendeeModalProps) {
  const [typeOptions, setTypeOptions] = useState<SelectOption[]>([]);
  const [countries, setCountries] = useState<{ id: number; code: string }[]>(
    [],
  );
  // phone is optional, so it starts valid; PhoneInput validates against the selected country
  const [phoneIsValid, setPhoneIsValid] = useState(true);
  const venueOptions = [
    { display: "None", value: "" },
    ...venues.map((venue) => ({
      display: venue.name,
      value: String(venue.id),
    })),
  ];

  const [initialAttendee] = useState<OtherAttendee>(() =>
    attendee ? _cloneDeep(attendee) : _cloneDeep(emptyAttendee),
  );
  const [currentAttendee, setCurrentAttendee] = useState<OtherAttendee>(() =>
    _cloneDeep(initialAttendee),
  );
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    SystemApi.list("tripEventOtherAttendeeTypes").then((types: EnumType[]) =>
      setTypeOptions(
        types.map((type) => ({
          display: type.displayValue,
          value: type.enumValue,
        })),
      ),
    );
    SystemApi.list("countries").then(setCountries);
  }, []);

  const onChange = (name: keyof OtherAttendee, value: string) => {
    setCurrentAttendee((prev) => ({ ...prev, [name]: value }));
  };

  const onChangeAssignedVenue = (value: string) => {
    setCurrentAttendee((prev) => ({
      ...prev,
      assignedVenueID: value ? Number(value) : undefined,
    }));
  };

  const isValid =
    !!currentAttendee.firstName &&
    !!currentAttendee.lastName &&
    !!currentAttendee.type &&
    phoneIsValid &&
    validateEmail(currentAttendee.email);

  const onSave = () => {
    setSubmitAttempted(true);

    if (!isValid) {
      return;
    }

    save(currentAttendee);
  };

  return (
    <Modal
      isOpen={true}
      className="other-attendees-modal-container modal-container"
    >
      <div className="modal-card card other-attendees-modal">
        <p className="large-text fw-500 mb-16">
          {!attendee ? "Add" : "Edit"} Attendee
        </p>

        <div className="flex flex-align-center mb-12">
          <label
            className="accent-text-dark required"
            style={{ width: "96px", minWidth: "96px" }}
          >
            First Name
          </label>
          <ValidatedInput
            className="full-width"
            input={<input type="text" className="full-width" />}
            name="firstName"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange("firstName", e.target.value)
            }
            required={true}
            showRequired={submitAttempted}
            value={currentAttendee.firstName}
          />
        </div>

        <div className="flex flex-align-center mb-12">
          <label
            className="accent-text-dark required"
            style={{ width: "96px", minWidth: "96px" }}
          >
            Last Name
          </label>
          <ValidatedInput
            className="full-width"
            input={<input type="text" className="full-width" />}
            name="lastName"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange("lastName", e.target.value)
            }
            required={true}
            showRequired={submitAttempted}
            value={currentAttendee.lastName}
          />
        </div>

        <div className="flex flex-align-center mb-12">
          <label
            className="accent-text-dark required"
            style={{ width: "96px", minWidth: "96px" }}
          >
            Type
          </label>
          <ValidatedInput
            className="full-width"
            input={<Select options={typeOptions} />}
            name="type"
            onChange={(name: keyof OtherAttendee, value: string) =>
              onChange(name, value)
            }
            required={true}
            showRequired={submitAttempted}
            value={currentAttendee.type}
          />
        </div>

        <div className="flex flex-align-center mb-12">
          <label
            className="accent-text-dark"
            style={{ width: "96px", minWidth: "96px" }}
          >
            Phone
          </label>
          <PhoneInput
            countries={countries}
            error={submitAttempted && !phoneIsValid}
            name="phoneNumber"
            onChange={(phone: string) => onChange("phoneNumber", phone)}
            validate={(valid: boolean) => setPhoneIsValid(valid)}
            validateCountry={false}
            value={currentAttendee.phoneNumber}
          />
        </div>

        <div className="flex mb-12">
          <label
            className="accent-text-dark"
            style={{ width: "96px", minWidth: "96px" }}
          >
            Email
          </label>
          <div className="full-width">
            <ValidatedInput
              input={<input type="text" className="full-width" />}
              name="email"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChange("email", e.target.value)
              }
              showValidation={submitAttempted}
              validate={(email: string) => validateEmail(email)}
              validationMessage="Invalid email address"
              value={currentAttendee.email}
            />
            <p className="accent-text small-text mt-4">
              If email is set, user will get the welcome email with QR code for
              check in. To avoid sending it, leave it blank.
            </p>
          </div>
        </div>

        <div className="flex mb-12">
          <label
            className="accent-text-dark"
            style={{ width: "96px", minWidth: "96px" }}
          >
            Venue
          </label>
          <div className="full-width">
            <ValidatedInput
              input={
                <Select options={venueOptions} placeholder="Select venue" />
              }
              name="assignedVenueID"
              onChange={(_name: string, value: string) =>
                onChangeAssignedVenue(value)
              }
              value={
                currentAttendee.assignedVenueID
                  ? String(currentAttendee.assignedVenueID)
                  : ""
              }
            />
            <p className="accent-text small-text mt-4">
              If the desired venue is not available, ensure it is saved to the
              event.
            </p>
          </div>
        </div>
        <div className="modal-btns flex flex-align-center flex-justify-end mt-16">
          <button
            className="btn link-text uppercase-text fw-500"
            onClick={close}
          >
            Cancel
          </button>
          <button
            className="btn link-text uppercase-text fw-500 ml-24"
            disabled={_isEqual(initialAttendee, currentAttendee)}
            onClick={onSave}
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
}
